/**
 * M95 — materialize long Stage 2 Grasslands levels (2-1…2-30).
 * Deterministic helpers only — no RNG.
 */

import type { StageDefinition, StageWaveSpec } from '../../combat/campaign/types'
import type { StageFirstClearRewards } from '../../combat/campaign/firstClear'
import type { DifficultyId } from '../../combat/types/primitives'
import { CHAPTER2_MAP_FOR_ORDINAL } from '../chapter2Maps'
import { chapter2LevelPlan } from './levelPlan'
import {
  completionRewardForWave,
  enemyLevelForWave,
  enemyRankForWave,
  spawnCountForWave,
  spawnIntervalForWave
} from './escalate'
import { bossWaveGroups, chapter2WaveId, g, overlapGroups, w } from './waveBuilders'

const ALL_DIFFICULTIES: DifficultyId[] = ['easy', 'normal', 'hard', 'insane']
const ALL_TOWERS = ['fire', 'frost', 'cannon', 'poison', 'aa', 'mage'] as const

/** Flatter first-clear curve for 30 long levels (M97 may refine further). */
function rewards(ordinal: number): StageFirstClearRewards {
  const base = 4 + Math.floor(ordinal * 0.55)
  const boss = ordinal % 5 === 0 ? 3 : 0
  const b = base + boss
  return {
    easy: b,
    normal: b * 2,
    hard: b * 4,
    insane: b * 8
  }
}

const COMPOSITION: Array<'normal' | 'fast' | 'mixed' | 'armor' | 'air' | 'special'> = [
  'normal',
  'fast',
  'mixed',
  'armor',
  'air',
  'special'
]

function pickEnemyIds(
  ordinal: number,
  waveIndex: number,
  allowAir: boolean
): string[] {
  const kind = COMPOSITION[(waveIndex + ordinal) % COMPOSITION.length]!
  if (kind === 'fast') return ['fast', 'normal']
  if (kind === 'armor') return ordinal >= 8 ? ['tank', 'normal'] : ['normal', 'fast']
  if (kind === 'air' && allowAir) return ['flyer', 'normal']
  if (kind === 'special') {
    // Gate resistant packs later so multi-route maps can establish before spikes.
    if (ordinal >= 18) return ['regen', 'shield']
    if (ordinal >= 12) return ['shield', 'fast']
    return ['fast', 'normal']
  }
  if (kind === 'mixed') return allowAir ? ['normal', 'flyer'] : ['normal', 'fast']
  return ['normal']
}

function buildWave(ordinal: number, waveIndex: number, waveCount: number, bossLevel: boolean): StageWaveSpec {
  const plan = chapter2LevelPlan(ordinal, CHAPTER2_MAP_FOR_ORDINAL[ordinal] ?? 'valley-v')
  const id = chapter2WaveId(ordinal, waveIndex)
  const reward = Math.max(18, Math.floor(completionRewardForWave(plan, waveIndex) * 0.5))
  const lv = Math.max(2, enemyLevelForWave(plan, waveIndex) - (ordinal <= 12 ? 1 : 0))
  const rank = enemyRankForWave(plan, waveIndex)
  // AIR after defenses stabilize — multi-route maps need longer ground-only openers.
  const allowAir = ordinal >= 8 && waveIndex >= Math.max(5, Math.floor(waveCount * 0.25))
  const isFinale = waveIndex === waveCount

  if (bossLevel && isFinale) {
    const support = allowAir
      ? [
          g('flyer', lv, 3, 0.75, 2.2, 'NORMAL'),
          g('tank', lv, 1, 1.35, 3.0, 'NORMAL'),
          g('fast', lv, 3, 0.6, 3.5)
        ]
      : [
          g('tank', lv, 1, 1.35, 2.0),
          g('fast', lv, 4, 0.6, 2.8),
          g('regen', lv, 2, 1.15, 3.5)
        ]
    const bossId = ordinal >= 25 && allowAir ? 'air_boss' : 'boss'
    return w(id, reward + 35, bossWaveGroups(bossId, Math.max(lv, 6), support, 1.0))
  }

  if (bossLevel && waveIndex >= waveCount - 1) {
    return w(
      id,
      reward + 12,
      overlapGroups([
        g('tank', lv, 1, 1.3, 0.3, rank),
        g('fast', lv, 3, 0.6, 0.7, rank),
        ...(allowAir ? [g('flyer', lv, 2, 0.7, 1.0)] : [g('shield', lv, 2, 1.2, 1.0)])
      ])
    )
  }

  const ids = pickEnemyIds(ordinal, waveIndex, allowAir)
  const multiRoute = ['confluence', 'twinspire', 'triad', 'delta', 'skew'].includes(
    CHAPTER2_MAP_FOR_ORDINAL[ordinal] ?? ''
  )
  const density =
    (ordinal <= 5 ? 0.4 : ordinal <= 12 ? 0.42 : ordinal <= 20 ? 0.48 : 0.52) *
    (multiRoute ? 0.85 : 1)
  const total = Math.max(2, Math.floor(spawnCountForWave(plan, waveIndex) * density))
  const interval =
    spawnIntervalForWave(plan, waveIndex) * (ordinal <= 15 ? 1.25 : 1.15) * (multiRoute ? 1.15 : 1)
  const per = Math.max(2, Math.ceil(total / ids.length))
  const groups = ids.map((enemyId, i) =>
    g(
      enemyId,
      Math.max(2, lv - (ordinal <= 10 ? 1 : 0)),
      per,
      interval,
      0.3 + i * 0.4,
      i === 0 && waveIndex > waveCount * 0.6 ? rank : 'NORMAL'
    )
  )
  return w(id, reward, groups)
}

export function materializeChapter2Stage(ordinal: number): StageDefinition {
  const mapId = CHAPTER2_MAP_FOR_ORDINAL[ordinal] ?? 'valley-v'
  const plan = chapter2LevelPlan(ordinal, mapId)
  const waves: StageWaveSpec[] = []
  for (let i = 1; i <= plan.waveCountTarget; i += 1) {
    waves.push(buildWave(ordinal, i, plan.waveCountTarget, plan.bossLevel))
  }
  return {
    id: `chapter2-stage${ordinal}`,
    chapterId: 'chapter-2',
    displayLabel: `2-${ordinal}`,
    mapId,
    waves,
    allowedTowerIds: [...ALL_TOWERS],
    availableDifficulties: ALL_DIFFICULTIES,
    firstClearRewards: rewards(ordinal),
    bossLevel: plan.bossLevel
  }
}

export function materializeAllChapter2Stages(): StageDefinition[] {
  return Array.from({ length: 30 }, (_, i) => materializeChapter2Stage(i + 1))
}
